import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaEye } from 'react-icons/fa';
import {
  fetchCampaignOpenToDisbursable,
  downloadCsv,
  CampaignOpenToDisbursableResponse,
  CampaignOpenToDisbursableRow,
} from '~/services/predefinedReports';
import { PredefinedResultsLayout, Column } from './PredefinedResultsLayout';

// Dates arrive as plain YYYY-MM-DD. Parsing at local midnight keeps them from
// sliding a day back in negative-offset timezones.
const formatDate = (value: string | null) =>
  value ? new Date(`${value}T00:00:00`).toLocaleDateString() : '—';

// A blank duration means the milestone hasn't happened yet, which is different
// from taking zero days — so it gets a dash, not a 0.
const formatDays = (value: number | null) => (value === null ? '—' : value);

const columns: Column<CampaignOpenToDisbursableRow>[] = [
  {
    key: 'view',
    label: '',
    render: (r) => (
      <Link
        to={`/campaigns/${r.campaign_id}`}
        target='_blank'
        rel='noopener noreferrer'
        title='View Campaign Details'
        className='inline-flex h-8 w-8 items-center justify-center rounded text-primary transition-colors duration-200 hover:bg-primary/10 hover:text-primary/80'
      >
        <FaEye size={16} />
      </Link>
    ),
  },
  { key: 'ntaf_id', label: 'NTAF ID' },
  { key: 'client_name', label: 'Client' },
  { key: 'csc_name', label: 'CSC' },
  { key: 'status', label: 'Status' },
  { key: 'campaign_open', label: 'Campaign Open', render: (r) => formatDate(r.campaign_open) },
  { key: 'first_donation', label: 'First Donation', render: (r) => formatDate(r.first_donation) },
  { key: 'disbursable', label: 'Disbursable', render: (r) => formatDate(r.disbursable) },
  {
    key: 'days_open_to_first_donation',
    label: 'Open → First Donation',
    rightAlign: true,
    render: (r) => formatDays(r.days_open_to_first_donation),
  },
  {
    key: 'days_open_to_disbursable',
    label: 'Open → Disbursable',
    rightAlign: true,
    render: (r) => formatDays(r.days_open_to_disbursable),
  },
];

const CampaignOpenToDisbursableResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<CampaignOpenToDisbursableResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = {
      from_date: searchParams.get('from_date') || undefined,
      to_date: searchParams.get('to_date') || undefined,
      csc: searchParams.get('csc') || undefined,
    };
    setLoading(true);
    fetchCampaignOpenToDisbursable(params)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => { setError(e.message); setData(null); })
      .finally(() => setLoading(false));
  }, [searchParams]);

  const range =
    data?.from_date && data?.to_date
      ? `Campaigns opened ${formatDate(data.from_date)} – ${formatDate(data.to_date)}`
      : 'Campaigns that have opened';

  return (
    <PredefinedResultsLayout
      title='Campaign Open to Disbursable'
      subtitle={range}
      columns={columns}
      rows={data?.rows ?? []}
      loading={loading}
      error={error}
      rowKey={(r) => r.campaign_id}
      onDownloadCsv={() => downloadCsv('campaign-open-to-disbursable', searchParams)}
    />
  );
};

export default CampaignOpenToDisbursableResults;
